import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/admin-constants";
import { Video, Plus, Trash2, Save, Loader2, ExternalLink, Info } from "lucide-react";

const ReeplayerInvitesManager = () => {
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  const load = useCallback(async () => {
    try {
      const [invRes, teamRes] = await Promise.all([
        axios.get(`${API}/reeplayer/invites`),
        axios.get(`${API}/schedule/teams`),
      ]);
      setInvites(invRes.data.invites || []);
      setIsDefault(invRes.data.is_default);
      setTeams(teamRes.data.teams || []);
    } catch {
      toast.error("Could not load invites");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateRow = (index, field, value) => {
    setInvites((prev) => prev.map((inv, i) => (i === index ? { ...inv, [field]: value } : inv)));
  };

  const addRow = () => {
    setInvites((prev) => [...prev, { invite_id: `new_${Date.now()}`, label: "", team: "", url: "" }]);
  };

  const removeRow = (index) => {
    setInvites((prev) => prev.filter((_, i) => i !== index));
  };

  const save = async () => {
    const invalid = invites.find((inv) => !inv.url?.trim() || !(inv.label?.trim() || inv.team?.trim()));
    if (invalid) {
      toast.error("Every invite needs a link and either a team or a label");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/reeplayer/invites`, { invites }, { withCredentials: true });
      setInvites(res.data.invites || []);
      setIsDefault(false);
      toast.success("Reeplayer invites saved — live on the Parent Hub");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-cfc-primary" /></div>;

  return (
    <div data-testid="reeplayer-invites-manager">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-cfc-primary">Reeplayer Invites</h1>
          <p className="text-sm text-cfc-text-muted mt-1">
            These appear on the Parent Hub at <a href="/parents" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">/parents</a> so families can join their team&apos;s live stream group.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" className="border-cfc-border" onClick={addRow} data-testid="add-invite-btn">
            <Plus size={14} className="mr-1.5" /> Add Invite
          </Button>
          <Button className="bg-cfc-primary text-white" onClick={save} disabled={saving} data-testid="save-invites-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />} Save
          </Button>
        </div>
      </div>

      {isDefault && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-blue-50 border border-blue-200 rounded-sm" data-testid="defaults-notice">
          <Info size={15} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800">
            Showing the club&apos;s original invite links. Pick a team for each one and hit Save — from then on this list is fully yours to edit.
          </p>
        </div>
      )}

      {invites.length === 0 ? (
        <Card className="border-cfc-border">
          <CardContent className="p-10 text-center">
            <Video size={28} className="mx-auto text-cfc-text-muted/40 mb-2" />
            <p className="text-sm text-cfc-text-muted">No invites yet — add your first Reeplayer group link.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {invites.map((inv, index) => (
            <Card key={inv.invite_id || index} className="border-cfc-border" data-testid={`invite-row-${index}`}>
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4">
                  <Label className="text-[11px] font-bold uppercase text-cfc-text-muted">TeamSnap Team</Label>
                  <select
                    value={inv.team || ""}
                    onChange={(e) => updateRow(index, "team", e.target.value)}
                    className="mt-1 w-full h-9 px-2 text-sm border border-cfc-border rounded-sm bg-white"
                    data-testid={`invite-team-${index}`}
                  >
                    <option value="">— Not linked to a team —</option>
                    {teams.map((t) => <option key={t.slug} value={t.team}>{t.team}</option>)}
                    {inv.team && !teams.some((t) => t.team === inv.team) && (
                      <option value={inv.team}>{inv.team} (not synced)</option>
                    )}
                  </select>
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[11px] font-bold uppercase text-cfc-text-muted">Display Label</Label>
                  <Input
                    value={inv.label || ""}
                    placeholder="e.g. U15"
                    onChange={(e) => updateRow(index, "label", e.target.value)}
                    className="mt-1 h-9"
                    data-testid={`invite-label-${index}`}
                  />
                </div>
                <div className="md:col-span-4">
                  <Label className="text-[11px] font-bold uppercase text-cfc-text-muted">Reeplayer Link</Label>
                  <Input
                    value={inv.url || ""}
                    placeholder="https://link.reeplayer.com/…"
                    onChange={(e) => updateRow(index, "url", e.target.value)}
                    className="mt-1 h-9"
                    data-testid={`invite-url-${index}`}
                  />
                </div>
                <div className="md:col-span-1 flex items-center gap-1 justify-end">
                  {inv.url && (
                    <a href={inv.url} target="_blank" rel="noreferrer" className="p-2 text-cfc-text-muted hover:text-cfc-primary" title="Open link" data-testid={`invite-open-${index}`}>
                      <ExternalLink size={14} />
                    </a>
                  )}
                  <button
                    onClick={() => removeRow(index)}
                    className="p-2 text-red-400 hover:text-red-600"
                    title="Remove invite"
                    data-testid={`invite-remove-${index}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-[11px] text-cfc-text-muted mt-4">
        Linking an invite to a team also shows it on that team&apos;s card on the Parent Hub. Leave the team blank to list it as a general channel.
      </p>
    </div>
  );
};

export default ReeplayerInvitesManager;
